import type { TBankOffers } from '@/components/App/NewOrder/offer-step';
import { BANKS_ICONS_NAMES, BankNames } from '@/constants';
import { formatPrice } from '@/services/formatter';
import type { OfferResponseFromApi } from '@/types/offers';

import { mapAltynOfferFromApi } from '../offer-step/features/altyn-bank/helpers/mapAltynOfferFromApi';
import { mapEuBankV2OfferFromApi } from '../offer-step/features/euras-bank-v2/helpers/mapEuBankV2OfferFromApi';
import { mapEuBankV1OfferFromApi } from '../offer-step/features/euras-bank/helpers/mapEuBankV1OfferFromApi';
import { mapHalykBankOfferFromApi } from '../offer-step/features/halyk-bank/helpers/mapHalykBankOfferFromApi';
import type { IBaseBankOffer } from '../offer-step/types/base-offer.types';

export const mapBankOfferFromApi = (
  response: OfferResponseFromApi
): TBankOffers => {
  const carModel = response?.request?.model?.name || '';
  const carBrand = response?.request?.model?.brand?.name || '';
  const carYear = response?.request?.car_year || '';
  const bankIconName =
    response?.bank ||
    BANKS_ICONS_NAMES[response?.bank?.name as keyof typeof BANKS_ICONS_NAMES];

  const factoryCheck = response.factory_check;

  const baseBankOffer: IBaseBankOffer = {
    id: response?.uuid || '',
    extId: response?.ext_uuid || '',
    offerId: response?.offered_uuid || '',
    offeredUuid: response?.offered_uuid || '',
    isOffered: response?.is_offered || false,
    bank: {
      ...(response?.bank ?? {}),
      fullname: response?.bank?.fullname || bankIconName?.name || '',
      icon: bankIconName?.icon || ''
    },
    hasApprovedDate: !!response?.approved_date,
    status: {
      name: response?.status?.name || '',
      code: response?.status?.code || '',
      uuid: response?.status?.uuid || ''
    },

    bankDescription: response?.bank_description || '',
    responseApprovedBody: response?.response_approved_body || null,
    responsePrimaryBody: response?.response_primary_body || '',
    responseSendedBody: response?.response_sended_body || '',

    factoryCheck: factoryCheck
      ? {
          allowManualConfirmation: factoryCheck.allowManualConfirmation,
          manuallyConfirmed: !!factoryCheck.manualConfirmation?.confirmed,
          reason: factoryCheck.reason,
          result: factoryCheck.result
        }
      : null,

    rate: String(response?.rate || ''),
    monthlyPayment: formatPrice(response?.monthly_payment),
    loanAmount: formatPrice(response?.loan_amount),
    insuranceAmount: formatPrice(response?.insurance_amount),
    initialPayment: formatPrice(
      response?.down_payment || response?.initial_payment
    ),
    loanPeriod: String(response?.loan_period || ''),
    product: `${carBrand} ${carModel}, ${carYear}`,
    productInfo: response?.request?.RequestsProducts
      ? response?.request?.RequestsProducts.find(
          (product) => product.product.bank_uuid === response.bank_uuid
        )
      : '',

    alternatives: response.Responses_Alternatives?.map((item) => ({
      uuid: item.uuid,
      extUuid: item.ext_uuid,
      responseUuid: item.response_uuid,
      offerId: item.response_uuid,
      offeredUuid: item.response_uuid,
      isOffered: false,

      description: item.description as string | undefined,

      monthlyPayment: formatPrice(item.monthly_payment),
      initialPayment: formatPrice(item.initial_payment),
      loanAmount: formatPrice(item.loan_amount),
      rate: item.rate,
      loanPeriod: String(item.loan_period),

      insuranceAmount: item.insurance_amount || 0,
      insuranceTerm: item.insurance_term || 0,
      insuranceName: item.insurance_name || ''
    }))
  };

  // TODO Для shinhan, sapa нужно создать свои
  // mapBankNameBankOfferFromApi. Они должны быть внутри
  // offer-step/features/bank-name/helpers/mapBankNameBankOfferFromApi.ts
  // Вырезать из store/offerStep.slice.ts форматирование по этим банкам и
  // перенести в соответствующие файлы.
  // Использовать их через мапу с передачей данных о предложении банка и возвращать.

  const bankMap: Partial<
    Record<
      BankNames,
      (
        baseBankOffer: IBaseBankOffer,
        response: OfferResponseFromApi
      ) => TBankOffers
    >
  > = {
    [BankNames.altyn]: mapAltynOfferFromApi,
    [BankNames.halyk]: mapHalykBankOfferFromApi,
    [BankNames.euBank]: mapEuBankV1OfferFromApi,
    [BankNames.euBankV2]: mapEuBankV2OfferFromApi
    // [BankNames.shinhan]: mapShinhanBankOfferFromApi,
    // [BankNames.sapa]: mapSapaBankOfferFromApi
  };

  const bankSpecialMapper = bankMap[response?.bank?.name];
  if (bankSpecialMapper) {
    return bankSpecialMapper(baseBankOffer, response);
  }

  // Для остальных банков использовать этот маппер, как базовый.
  return {
    ...baseBankOffer,
    bankName: response?.bank?.name // Для правильной типизации между банками
    // Форматтер универсален и не знает тип банка на этапе компиляции.
    // Соответствие TBccBankOffer | TDefaultBankOffer гарантируется в runtime
    // через дискриминант bankName, который приходит из API.
  } as TBankOffers;
};
